import { isNil } from 'lodash-es'

import {
  HostPayBadge,
  TContractStatus,
  ZContractStatus,
} from '@/entities/contract'
import { formatCurrency, isEmptyString } from '@/shared/lib'
import { Separator, ContentBox, Description } from '@/shared/ui'

export type Props = {
  status: TContractStatus
  usingFee: number
  longTermDiscount: number
  earlyDiscount: number
  mgmtFee: number
  cleanFee: number
  serviceFee: number
  totalUsingFee: number
  isCancelled: boolean
  cancelFee: number | null
  cancelFeePercent: number | null
  isExtend: boolean
  hostPayAmount: number
  hostPayAmountBeforeOtherFee: number | null
  hostPayTime: string | null
  roomId: number
  showRefundInformation: boolean
  hostPayDescription: string
  refundAmount: number | null
}

export const HostPayInformationDetail = ({
  status,
  usingFee,
  longTermDiscount,
  earlyDiscount,
  mgmtFee,
  cleanFee,
  serviceFee,
  totalUsingFee,
  cancelFee,
  cancelFeePercent,
  isCancelled,
  isExtend,
  hostPayAmount,
  hostPayAmountBeforeOtherFee,
  hostPayTime,
  showRefundInformation,
  hostPayDescription,
  refundAmount,
}: Props) => {
  const showLongTermDiscount = longTermDiscount > 0 && !showRefundInformation
  const showEarlyDiscount = earlyDiscount > 0 && !showRefundInformation
  const showHostPayTime = hostPayTime !== null
  const showHostPayDescription = !isEmptyString(hostPayDescription)

  const isApproveReadyOrPayReady =
    status === ZContractStatus.Enum.APPROVE_READY ||
    status === ZContractStatus.Enum.PAY_READY

  const payAmountLabel = isApproveReadyOrPayReady
    ? '정산 예정 금액'
    : '최종 정산 금액'

  return (
    <div className="space-y-3">
      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel semiBold className="typo-label1 text-black">
            이용 금액
          </Description.ItemLabel>
          <Description.ItemValue
            bold
            className="typo-headline3"
            lineThrough={isCancelled}
          >
            {formatCurrency(totalUsingFee)}원
          </Description.ItemValue>
        </Description.Item>
        {showRefundInformation && !isNil(refundAmount) && (
          <Description.Item className="justify-between">
            <Description.ItemLabel semiBold className="typo-label1 text-black">
              임차인 환불 금액
            </Description.ItemLabel>
            <Description.ItemValue bold className="typo-headline3">
              {formatCurrency(refundAmount, { negative: true })}원
            </Description.ItemValue>
          </Description.Item>
        )}
        <Description.Item
          className="justify-between"
          label={
            showRefundInformation
              ? `임대료 (${cancelFeePercent}% 환불)`
              : '임대료'
          }
          value={
            showRefundInformation && !isNil(cancelFee)
              ? `${formatCurrency(cancelFee, { negative: true })}원`
              : `${formatCurrency(usingFee)}원`
          }
          lineThrough={isCancelled}
        />

        {showLongTermDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel>장기계약 할인</Description.ItemLabel>
            <Description.ItemValue lineThrough={isCancelled}>
              {formatCurrency(longTermDiscount, { negative: true })}원
            </Description.ItemValue>
          </Description.Item>
        )}

        {showEarlyDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel>즉시입주 할인</Description.ItemLabel>
            <Description.ItemValue lineThrough={isCancelled}>
              {formatCurrency(earlyDiscount, { negative: true })}원
            </Description.ItemValue>
          </Description.Item>
        )}

        <Description.Item
          className="justify-between"
          label="관리비"
          value={`${formatCurrency(mgmtFee, { negative: showRefundInformation })}원`}
          lineThrough={isCancelled}
        />

        <Description.Item className="items-start justify-between">
          <Description.ItemLabel>
            <span>청소비</span>
            {isExtend && (
              <span className="typo-label2 block font-normal">
                * 연장계약에서는 청소비가 청구되지 않습니다.
              </span>
            )}
          </Description.ItemLabel>
          <Description.ItemValue lineThrough={isCancelled}>
            {formatCurrency(cleanFee, { negative: showRefundInformation })}원
          </Description.ItemValue>
        </Description.Item>
      </Description.List>

      <Separator />

      <Description.List>
        {showRefundInformation && !isNil(hostPayAmountBeforeOtherFee) && (
          <Description.Item className="justify-between">
            <Description.ItemLabel semiBold className="typo-label1 text-black">
              정산 대상 금액
            </Description.ItemLabel>
            <Description.ItemValue bold className="typo-headline3">
              {formatCurrency(hostPayAmountBeforeOtherFee)}원
            </Description.ItemValue>
          </Description.Item>
        )}
        <Description.Item
          className="justify-between"
          label="계약 수수료"
          value={`${formatCurrency(serviceFee, { negative: true })}원`}
          lineThrough={isCancelled}
        />
      </Description.List>

      <Separator color="black" />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel bold className="typo-body1-reading text-black">
            {payAmountLabel}
          </Description.ItemLabel>
          <Description.ItemValue bold highlight className="typo-headline1">
            {formatCurrency(hostPayAmount)}원
          </Description.ItemValue>
        </Description.Item>
        {showHostPayDescription && (
          <ContentBox color="purple" className="px-3" align="center">
            * {hostPayDescription}
          </ContentBox>
        )}
        {showHostPayTime && (
          <div className="flex items-center justify-end gap-2">
            <span className="typo-label2 text-black">{hostPayTime}</span>
            <HostPayBadge />
          </div>
        )}
      </Description.List>
    </div>
  )
}
